import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import UnitConverter from "../components/UnitConverter.jsx";
import {
  useInventoryItemQuery,
  useInventoryLogQuery,
  useUpdateInventoryItem,
  useDeleteInventoryItem,
  useAdjustInventory,
  useIngredientsQuery,
  useAddIngredient,
  useRemoveIngredient,
  useManufactureItem,
  useInventoryYieldStats,
  useInventoryQuery,
  useReferralLinksQuery,
  useInventoryLineageQuery,
} from "../hooks/useInventoryQuery.js";
import { useDashboardQuery } from "../hooks/useDashboardQuery.js";
import { UNIT_GROUPS, toBaseValue, fromBaseValue, dbUnitToDisplay, storageHint } from "../utility/unitConversions.js";
import { computeVPD } from "../utility/vpd.js";
import { BIO_CATEGORIES, BOM_CATEGORIES, PARENT_CATEGORIES, CATEGORY_LABELS, CATEGORY_ORDER } from "../utility/inventoryConstants.js";

const METRIC_UNITS = { temperature: "°F", humidity: "%", co2: "ppm" };
const METRIC_LABELS = { temperature: "Temp", humidity: "RH", co2: "CO2" };

function metricColor(value, limitLow, limitHigh) {
  if (value == null) return "text-gray-400";
  if (value < limitLow) return "text-blue-700";
  if (value > limitHigh) return "text-red-600";
  return "text-green-600";
}

function vpdColor(vpd) {
  if (vpd == null) return "text-gray-400";
  if (vpd < 0.1) return "text-blue-700";
  if (vpd < 0.5) return "text-green-600";
  if (vpd < 0.9) return "text-yellow-500";
  return "text-red-600";
}

const STATUS_COLORS = {
  growing: "bg-blue-100 text-blue-700",
  ready: "bg-green-100 text-green-700",
  depleted: "bg-gray-100 text-gray-500",
  contaminated: "bg-red-100 text-red-700",
};

const CATEGORIES = CATEGORY_ORDER.map((v) => ({ value: v, label: CATEGORY_LABELS[v] }));

const CONTAINER_TYPES = ["syringe", "mason_jar", "agar_plate", "grain_jar", "bag", "other"];

const ADJUST_REASONS = [
  { value: "restock", label: "Restock" },
  { value: "manual_adjustment", label: "Manual adjustment" },
  { value: "contamination_loss", label: "Contamination loss" },
];

function UnitSelect({ value, onChange, className }) {
  return (
    <select value={value} onChange={onChange} className={className}>
      {UNIT_GROUPS.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.units.map((u) => (
            <option key={u.display} value={u.display}>{u.display}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString();
}

function fmtQty(value, dbUnit) {
  if (value == null) return "—";
  const n = Number(value);
  return `${Number.isInteger(n) ? n : n.toFixed(2)} ${dbUnit}`;
}

function fmtDelta(n, dbUnit) {
  const prefix = n > 0 ? "+" : "";
  const display = Number.isInteger(n) ? n : Number(n).toFixed(2);
  return `${prefix}${display} ${dbUnit}`;
}

export default function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const itemId = Number(id);
  const qc = useQueryClient();

  const { data: item, isLoading } = useInventoryItemQuery(itemId);
  const { data: logs = [] } = useInventoryLogQuery(itemId);
  const { data: dashboard } = useDashboardQuery();
  const { data: ingredients = [] } = useIngredientsQuery(itemId);
  const { data: yieldStats } = useInventoryYieldStats(itemId);
  const { data: allItems = [] } = useInventoryQuery();
  const { data: referralLinks = [] } = useReferralLinksQuery();
  const { data: lineage } = useInventoryLineageQuery(itemId);

  const updateItem = useUpdateInventoryItem(itemId);
  const deleteItem = useDeleteInventoryItem();
  const adjust = useAdjustInventory(itemId);
  const addIngredient = useAddIngredient(itemId);
  const removeIngredient = useRemoveIngredient(itemId);
  const manufacture = useManufactureItem(itemId);

  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [editSaveError, setEditSaveError] = useState(null);
  const [editDisplayUnit, setEditDisplayUnit] = useState("syringes");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustDisplayUnit, setAdjustDisplayUnit] = useState(null);
  const [adjustReason, setAdjustReason] = useState("manual_adjustment");
  const [adjustNotes, setAdjustNotes] = useState("");
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustError, setAdjustError] = useState(null);

  const [editCostMode, setEditCostMode] = useState("direct"); // "direct" | "calc"
  const [editCostQty, setEditCostQty] = useState("");
  const [editCostTotal, setEditCostTotal] = useState("");

  // BOM ingredient add form
  const [newIngredientId, setNewIngredientId] = useState("");
  const [newIngredientQty, setNewIngredientQty] = useState("");
  const [ingredientError, setIngredientError] = useState(null);

  // Manufacture modal
  const [showManufacture, setShowManufacture] = useState(false);
  const [manufactureQty, setManufactureQty] = useState("1");
  const [shortfalls, setShortfalls] = useState(null);
  const [manufactureError, setManufactureError] = useState(null);

  if (isLoading) {
    return (
      <LayoutWide title="Inventory">
        <p className="text-sm text-gray-400 p-6">Loading…</p>
      </LayoutWide>
    );
  }
  if (!item) {
    return (
      <LayoutWide title="Inventory">
        <p className="text-sm text-red-500 p-6">Item not found.</p>
      </LayoutWide>
    );
  }

  const isBio = BIO_CATEGORIES.has(item.category);
  const isBom = BOM_CATEGORIES.has(item.category);
  const editIsBio = BIO_CATEGORIES.has(editFields.category ?? item.category);
  const editShowParent = PARENT_CATEGORIES.has(editFields.category ?? item.category);
  const editShowExpiry = !BOM_CATEGORIES.has(editFields.category ?? item.category);
  const itemDbUnit = item.unit;
  const activeAdjustUnit = adjustDisplayUnit ?? dbUnitToDisplay(itemDbUnit);

  // Reorder URL: prefer admin referral link, fall back to source_url
  const reorderUrl = item.referral_link_url || item.source_url || null;
  const isLowOrDepleted = item.low_stock_threshold != null
    ? item.quantity <= item.low_stock_threshold
    : item.status === "depleted";

  // Items eligible as ingredients (supplies/other from same list, not self)
  const ingredientOptions = allItems.filter(
    (i) => i.id !== itemId && (i.category === "materials" || i.category === "supplies")
  );

  function toDateInput(dateStr) {
    if (!dateStr) return "";
    return new Date(dateStr).toISOString().slice(0, 10);
  }

  function startEdit() {
    const displayUnit = dbUnitToDisplay(item.unit);
    setEditDisplayUnit(displayUnit);
    setEditSaveError(null);
    setEditFields({
      name: item.name,
      category: item.category,
      species: item.species ?? "",
      generation: item.generation ?? "",
      status: item.status,
      container_type: item.container_type ?? "",
      low_stock_threshold: item.low_stock_threshold != null
        ? String(fromBaseValue(item.low_stock_threshold, displayUnit))
        : "",
      started_at: toDateInput(item.started_at),
      inoculated_at: toDateInput(item.inoculated_at),
      ready_at: toDateInput(item.ready_at),
      source_name: item.source_name ?? "",
      source_url: item.source_url ?? "",
      referral_link_id: item.referral_link_id ? String(item.referral_link_id) : "",
      default_deduct_qty: item.default_deduct_qty != null ? String(item.default_deduct_qty) : "",
      notes: item.notes ?? "",
      parent_item_id: item.parent_item_id ?? null,
      cost_per_unit: item.cost_per_unit != null ? String(item.cost_per_unit) : "",
      expires_at: item.expires_at ? toDateInput(item.expires_at) : "",
    });
    setEditCostMode("direct");
    setEditCostQty("");
    setEditCostTotal("");
    setEditing(true);
  }

  async function saveEdit() {
    setEditSaveError(null);
    const previousParentId = item.parent_item_id ?? null;
    const body = { ...editFields };
    if (body.low_stock_threshold === "") body.low_stock_threshold = null;
    else body.low_stock_threshold = toBaseValue(parseFloat(body.low_stock_threshold), editDisplayUnit);
    if (!body.container_type) body.container_type = null;
    if (!body.started_at) body.started_at = null;
    if (!body.inoculated_at) body.inoculated_at = null;
    if (!body.ready_at) body.ready_at = null;
    if (!body.source_name) body.source_name = null;
    if (!body.source_url) body.source_url = null;
    if (!body.species) body.species = null;
    if (!body.generation) body.generation = null;
    if (!body.notes) body.notes = null;
    body.referral_link_id = body.referral_link_id ? Number(body.referral_link_id) : null;
    body.default_deduct_qty = body.default_deduct_qty ? parseFloat(body.default_deduct_qty) : null;
    body.parent_item_id = body.parent_item_id ? Number(body.parent_item_id) : null;
    if (editCostMode === "calc") {
      const q = parseFloat(editCostQty);
      const t = parseFloat(editCostTotal);
      body.cost_per_unit = (q > 0 && !isNaN(t)) ? t / q : null;
    } else {
      body.cost_per_unit = body.cost_per_unit ? parseFloat(body.cost_per_unit) : null;
    }
    if (!body.expires_at) body.expires_at = null;
    delete body.unit;
    try {
      await updateItem.mutateAsync(body);
      const newParentId = body.parent_item_id;
      if (newParentId !== previousParentId) {
        if (previousParentId) qc.invalidateQueries({ queryKey: ["inventory", previousParentId, "lineage"] });
        if (newParentId) qc.invalidateQueries({ queryKey: ["inventory", newParentId, "lineage"] });
      }
      setEditing(false);
    } catch (err) {
      setEditSaveError(err.message ?? "Save failed");
    }
  }

  async function handleDelete() {
    await deleteItem.mutateAsync(itemId);
    navigate("/inventory");
  }

  async function handleAdjust(quickDelta) {
    setAdjustError(null);
    const rawDelta = quickDelta !== undefined ? quickDelta : parseFloat(adjustDelta);
    if (!rawDelta || rawDelta === 0) {
      setAdjustError("Delta must be non-zero");
      return;
    }
    const delta = toBaseValue(rawDelta, activeAdjustUnit);
    try {
      await adjust.mutateAsync({ delta, reason: adjustReason, notes: adjustNotes });
      setShowAdjustModal(false);
      setAdjustDelta("");
      setAdjustNotes("");
      setAdjustDisplayUnit(null);
    } catch (err) {
      setAdjustError(err.message ?? "Adjust failed");
    }
  }

  async function handleAddIngredient(e) {
    e.preventDefault();
    setIngredientError(null);
    if (!newIngredientId || !newIngredientQty) {
      setIngredientError("Select an ingredient and enter a quantity");
      return;
    }
    try {
      await addIngredient.mutateAsync({ ingredient_id: Number(newIngredientId), quantity: parseFloat(newIngredientQty) });
      setNewIngredientId("");
      setNewIngredientQty("");
    } catch (err) {
      setIngredientError(err.message ?? "Failed to add ingredient");
    }
  }

  async function handleManufacture(force) {
    setManufactureError(null);
    const qty = parseFloat(manufactureQty);
    if (!qty || qty <= 0) {
      setManufactureError("Enter a positive quantity");
      return;
    }
    try {
      await manufacture.mutateAsync({ quantity: qty, force: !!force });
      setShortfalls(null);
      setShowManufacture(false);
      setManufactureQty("1");
    } catch (err) {
      if (err?.body?.shortfalls) {
        setShortfalls(err.body.shortfalls);
      } else {
        setManufactureError(err.message ?? "Manufacture failed");
      }
    }
  }

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";
  const selectCls = `${inputCls} bg-white`;

  return (
    <LayoutWide title={item.name}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/inventory")}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Back"
            >
              ←
            </button>
            <div>
              {editing ? (
                <input
                  value={editFields.name}
                  onChange={(e) => setEditFields({ ...editFields, name: e.target.value })}
                  className="text-2xl font-semibold text-cyan-800 border-b border-cyan-400 bg-transparent focus:outline-none"
                />
              ) : (
                <h1 className="text-2xl font-semibold text-cyan-800">{item.name}</h1>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs rounded-full px-2 py-0.5 bg-cyan-100 text-cyan-700 font-medium">
                  {CATEGORY_LABELS[item.category] ?? item.category}
                </span>
                <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${STATUS_COLORS[item.status] ?? "bg-gray-100 text-gray-600"}`}>
                  {editing ? (
                    <select
                      value={editFields.status}
                      onChange={(e) => setEditFields({ ...editFields, status: e.target.value })}
                      className="bg-transparent text-xs focus:outline-none"
                    >
                      {["growing", "ready", "depleted", "contaminated"].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : item.status}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {/* Reorder button */}
            {!editing && reorderUrl && (isLowOrDepleted || true) && (
              <a
                href={reorderUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${isLowOrDepleted ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Reorder
              </a>
            )}
            {editing ? (
              <>
                <button
                  onClick={saveEdit}
                  disabled={updateItem.isPending}
                  className="bg-cyan-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-cyan-700 disabled:opacity-50"
                >
                  {updateItem.isPending ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => { setEditing(false); setEditSaveError(null); }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                {editSaveError && (
                  <span className="text-xs text-red-600">{editSaveError}</span>
                )}
              </>
            ) : (
              <button
                onClick={startEdit}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Sensor teaser */}
        {item.device_id && dashboard?.devices?.[item.device_id] && (() => {
          const { device, sensors, thresholds } = dashboard.devices[item.device_id];
          const thresh = thresholds?.thresholds ?? {};
          return (
            <div className="rounded-xl bg-white shadow p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {device.display_name || device.hostname}
                </p>
                <span className="text-xs text-gray-400">
                  {device.last_seen ? `Updated ${new Date(device.last_seen).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "No data"}
                </span>
              </div>
              <div className="space-y-2">
                {sensors.map(sensor => {
                  const isOrange = sensor.sensor_code === "orange1";
                  const tempMetric = sensor.metrics.find(m => m.metric === "temperature");
                  const humMetric = sensor.metrics.find(m => m.metric === "humidity");
                  const vpd = (tempMetric?.latest_value != null && humMetric?.latest_value != null)
                    ? computeVPD(tempMetric.latest_value, humMetric.latest_value) : null;
                  return (
                    <div key={sensor.id} className="flex items-center gap-4 py-1.5 border-t border-gray-50 first:border-0">
                      <div className="flex items-center gap-2 w-36 shrink-0">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${isOrange ? "bg-orange-500" : "bg-cyan-500"}`} />
                        <span className="text-sm font-medium text-gray-700 truncate">
                          {sensor.display_name || sensor.sensor_name}
                        </span>
                      </div>
                      <div className="flex gap-5 flex-wrap">
                        {sensor.metrics.map(metric => {
                          const t = thresh[metric.metric];
                          const color = t ? metricColor(metric.latest_value, t.low_value, t.high_value) : "text-gray-600";
                          return (
                            <div key={metric.id} className="text-center min-w-[48px]">
                              <div className={`text-base font-bold tabular-nums ${color}`}>
                                {metric.latest_value != null ? Math.round(metric.latest_value * 10) / 10 : "--"}
                                <span className="text-xs font-normal ml-0.5">{METRIC_UNITS[metric.metric]}</span>
                              </div>
                              <div className="text-xs text-gray-400">{METRIC_LABELS[metric.metric]}</div>
                            </div>
                          );
                        })}
                        {vpd != null && (
                          <div className="text-center min-w-[48px]">
                            <div className={`text-base font-bold tabular-nums ${vpdColor(vpd)}`}>
                              {vpd.toFixed(2)}<span className="text-xs font-normal ml-0.5">kPa</span>
                            </div>
                            <div className="text-xs text-gray-400">VPD</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <p className="text-xs text-gray-400 mb-0.5">Quantity</p>
            <p className="text-lg font-semibold text-gray-800">
              {fmtQty(item.quantity, itemDbUnit)}
            </p>
          </div>
          {isBio && (
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-xs text-gray-400 mb-0.5">Container</p>
              {editing ? (
                <select
                  value={editFields.container_type}
                  onChange={(e) => setEditFields({ ...editFields, container_type: e.target.value })}
                  className="w-full bg-transparent text-sm focus:outline-none border-b border-gray-200"
                >
                  <option value="">— none —</option>
                  {CONTAINER_TYPES.map((c) => (
                    <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-700">
                  {item.container_type ? item.container_type.replace(/_/g, " ") : "—"}
                </p>
              )}
            </div>
          )}
          {isBio && (
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-xs text-gray-400 mb-0.5">Started</p>
              {editing ? (
                <input type="date" value={editFields.started_at} onChange={(e) => setEditFields({ ...editFields, started_at: e.target.value })} className="w-full text-sm focus:outline-none" />
              ) : (
                <p className="text-sm font-medium text-gray-700">{fmt(item.started_at)}</p>
              )}
            </div>
          )}
          {isBio && (
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-xs text-gray-400 mb-0.5">Ready</p>
              {editing ? (
                <input type="date" value={editFields.ready_at} onChange={(e) => setEditFields({ ...editFields, ready_at: e.target.value })} className="w-full text-sm focus:outline-none" />
              ) : (
                <p className="text-sm font-medium text-gray-700">{fmt(item.ready_at)}</p>
              )}
            </div>
          )}
        </div>

        {/* Edit-only fields: category, inoculated date, parent, cost, expiry */}
        {editing && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Category</label>
                <select value={editFields.category} onChange={(e) => setEditFields({ ...editFields, category: e.target.value })} className={selectCls}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              {editIsBio && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Inoculated</label>
                  <input type="date" value={editFields.inoculated_at} onChange={(e) => setEditFields({ ...editFields, inoculated_at: e.target.value })} className={inputCls} />
                </div>
              )}
            </div>
            {editShowParent && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Spawned from</label>
                <select
                  value={editFields.parent_item_id ?? ""}
                  onChange={(e) => setEditFields({ ...editFields, parent_item_id: e.target.value ? Number(e.target.value) : null })}
                  className={selectCls}
                >
                  <option value="">— none —</option>
                  {allItems
                    .filter((i) => i.id !== itemId && BIO_CATEGORIES.has(i.category) && i.status !== "depleted" && i.status !== "contaminated")
                    .map((i) => (
                      <option key={i.id} value={i.id}>{i.name} — {CATEGORY_LABELS[i.category] ?? i.category}</option>
                    ))}
                </select>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-gray-400">Cost per {item.unit}</label>
                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={() => setEditCostMode("direct")}
                    className={`px-2 py-0.5 rounded ${editCostMode === "direct" ? "bg-cyan-100 text-cyan-700 font-medium" : "text-gray-400 hover:text-gray-600"}`}>
                    Direct
                  </button>
                  <button type="button" onClick={() => setEditCostMode("calc")}
                    className={`px-2 py-0.5 rounded ${editCostMode === "calc" ? "bg-cyan-100 text-cyan-700 font-medium" : "text-gray-400 hover:text-gray-600"}`}>
                    Qty ÷ Total
                  </button>
                </div>
              </div>
              {editCostMode === "direct" ? (
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={editFields.cost_per_unit}
                  onChange={(e) => setEditFields({ ...editFields, cost_per_unit: e.target.value })}
                  placeholder="Optional"
                  className={inputCls}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={editCostQty}
                    onChange={(e) => setEditCostQty(e.target.value)}
                    placeholder={`Qty (${item.unit})`}
                    className={inputCls}
                  />
                  <span className="text-gray-400 text-sm shrink-0">÷</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={editCostTotal}
                    onChange={(e) => setEditCostTotal(e.target.value)}
                    placeholder="Total cost $"
                    className={inputCls}
                  />
                  <span className="text-gray-400 text-sm shrink-0">=</span>
                  <span className="text-sm font-medium text-cyan-700 whitespace-nowrap tabular-nums">
                    {editCostQty && editCostTotal && parseFloat(editCostQty) > 0
                      ? `$${(parseFloat(editCostTotal) / parseFloat(editCostQty)).toFixed(4)}`
                      : "—"}
                  </span>
                </div>
              )}
            </div>
            {editShowExpiry && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Expires</label>
                <input
                  type="date"
                  value={editFields.expires_at}
                  onChange={(e) => setEditFields({ ...editFields, expires_at: e.target.value })}
                  className={inputCls}
                />
              </div>
            )}
          </div>
        )}

        {/* Species / generation (bio only) */}
        {isBio && (item.species || item.generation || editing) && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
            {editing ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Species</label>
                  <input value={editFields.species} onChange={(e) => setEditFields({ ...editFields, species: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Generation</label>
                  <input value={editFields.generation} onChange={(e) => setEditFields({ ...editFields, generation: e.target.value })} className={inputCls} />
                </div>
              </div>
            ) : (
              <>
                {item.species && <p className="text-sm italic text-gray-600">{item.species}</p>}
                {item.generation && <p className="text-xs text-gray-400 mt-0.5">{item.generation}</p>}
              </>
            )}
          </div>
        )}

        {/* Notes */}
        {(item.notes || editing) && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
            <p className="text-xs text-gray-400 mb-1">Notes</p>
            {editing ? (
              <textarea value={editFields.notes} onChange={(e) => setEditFields({ ...editFields, notes: e.target.value })} rows={3} className={inputCls} />
            ) : (
              <p className="text-sm text-gray-700 whitespace-pre-line">{item.notes}</p>
            )}
          </div>
        )}

        {/* Unit converter (edit mode) */}
        {editing && (
          <div className="mb-4">
            <UnitConverter />
          </div>
        )}

        {/* Low stock threshold + default deduct qty (edit mode) */}
        {editing && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4 space-y-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Low stock threshold ({editDisplayUnit})
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={editFields.low_stock_threshold}
                onChange={(e) => setEditFields({ ...editFields, low_stock_threshold: e.target.value })}
                className={inputCls}
              />
              {storageHint(editDisplayUnit) && (
                <p className="text-xs text-gray-400 mt-1">{storageHint(editDisplayUnit)}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Default deduct quantity ({editDisplayUnit})
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={editFields.default_deduct_qty}
                onChange={(e) => setEditFields({ ...editFields, default_deduct_qty: e.target.value })}
                placeholder="e.g. 1"
                className={inputCls}
              />
            </div>
          </div>
        )}

        {/* Source / reorder */}
        {(item.source_name || item.source_url || item.referral_link_id || editing) && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
            <p className="text-xs text-gray-400 mb-2">Source / Reorder</p>
            {editing ? (
              <div className="space-y-3">
                {referralLinks.length > 0 && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Reorder link</label>
                    <select
                      value={editFields.referral_link_id}
                      onChange={(e) => setEditFields({ ...editFields, referral_link_id: e.target.value })}
                      className={selectCls}
                    >
                      <option value="">— none —</option>
                      {referralLinks.map((l) => (
                        <option key={l.id} value={l.id}>{l.name}{l.supplier ? ` (${l.supplier})` : ""}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Source name</label>
                  <input value={editFields.source_name} onChange={(e) => setEditFields({ ...editFields, source_name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Source URL</label>
                  <input type="url" value={editFields.source_url} onChange={(e) => setEditFields({ ...editFields, source_url: e.target.value })} className={inputCls} />
                </div>
              </div>
            ) : (
              <>
                {item.source_name && <p className="text-sm text-gray-700">{item.source_name}</p>}
                {item.source_url && (
                  <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-600 hover:underline break-all">
                    {item.source_url}
                  </a>
                )}
              </>
            )}
          </div>
        )}

        {/* Parent lineage link (view mode) */}
        {!editing && item.parent_item_name && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
            <p className="text-xs text-gray-400 mb-1">Spawned from</p>
            <button
              onClick={() => navigate(`/inventory/${item.parent_item_id}`)}
              className="text-sm text-cyan-600 hover:underline"
            >
              ← {item.parent_item_name}
            </button>
          </div>
        )}

        {/* Lineage section */}
        {lineage && (lineage.ancestors.length > 0 || lineage.children.length > 0) && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
            <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Lineage</p>
            {lineage.ancestors.length > 0 && (
              <div className="mb-2">
                <p className="text-xs text-gray-400 mb-1">Ancestors</p>
                <div className="flex flex-wrap gap-2">
                  {lineage.ancestors.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/inventory/${a.id}`)}
                      className="text-xs text-cyan-600 hover:underline"
                    >
                      ← {a.name}
                    </button>
                  ))}
                  {lineage.truncated && <span className="text-xs text-gray-400">(chain continues…)</span>}
                </div>
              </div>
            )}
            {lineage.children.length > 0 && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Spawned</p>
                <div className="flex flex-wrap gap-2">
                  {lineage.children.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => navigate(`/inventory/${c.id}`)}
                      className="text-xs text-cyan-600 hover:underline"
                    >
                      → {c.name}
                    </button>
                  ))}
                  {lineage.truncated && lineage.ancestors.length === 0 && (
                    <span className="text-xs text-gray-400">(more not shown)</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cost and expiry stats */}
        {(item.cost_per_unit != null || item.expires_at) && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {item.cost_per_unit != null && (
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-xs text-gray-400 mb-0.5">Cost / {item.unit}</p>
                <p className="text-sm font-medium text-gray-800">{item.cost_per_unit}</p>
              </div>
            )}
            {item.expires_at && (
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-xs text-gray-400 mb-0.5">Expires</p>
                <p className={`text-sm font-medium ${new Date(item.expires_at) < new Date(Date.now() + 14 * 86400 * 1000) ? "text-red-600" : "text-gray-800"}`}>
                  {fmt(item.expires_at)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Yield stats */}
        {yieldStats && yieldStats.grow_count > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
            <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Harvest stats</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-400">Grows</p>
                <p className="text-base font-semibold text-gray-800">{yieldStats.grow_count}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Total harvested</p>
                <p className="text-base font-semibold text-gray-800">{yieldStats.total_weight_g.toFixed(0)}g</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Avg per grow</p>
                <p className="text-base font-semibold text-gray-800">{yieldStats.avg_weight_g.toFixed(0)}g</p>
              </div>
            </div>
          </div>
        )}

        {/* BOM ingredients section (work_in_progress and products) */}
        {isBom && (
          <div className="bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">Ingredients (BOM)</p>
              {ingredients.length > 0 && (
                <button
                  onClick={() => { setShowManufacture(true); setShortfalls(null); setManufactureError(null); setManufactureQty("1"); }}
                  className="text-xs bg-cyan-600 text-white rounded-md px-2.5 py-1 hover:bg-cyan-700"
                >
                  Log production
                </button>
              )}
            </div>

            {ingredients.length === 0 ? (
              <p className="text-xs text-gray-400 px-4 py-3">No ingredients defined. Add some below to enable manufacturing.</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {ingredients.map((ing) => (
                  <div key={ing.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                    <span className="text-gray-700">{ing.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{ing.quantity} {ing.unit} per unit</span>
                      <button
                        onClick={() => removeIngredient.mutate(ing.ingredient_id)}
                        className="text-red-400 hover:text-red-600 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add ingredient form */}
            <form onSubmit={handleAddIngredient} className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Ingredient</label>
                <select
                  value={newIngredientId}
                  onChange={(e) => setNewIngredientId(e.target.value)}
                  className={selectCls}
                >
                  <option value="">— select —</option>
                  {ingredientOptions.map((i) => (
                    <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
                  ))}
                </select>
              </div>
              <div className="w-28">
                <label className="block text-xs text-gray-400 mb-1">Qty per unit</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={newIngredientQty}
                  onChange={(e) => setNewIngredientQty(e.target.value)}
                  className={inputCls}
                />
              </div>
              <button
                type="submit"
                disabled={addIngredient.isPending}
                className="px-3 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 whitespace-nowrap"
              >
                Add
              </button>
            </form>
            {ingredientError && <p className="text-xs text-red-600 px-4 pb-2">{ingredientError}</p>}
          </div>
        )}

        {/* Manufacture modal */}
        {showManufacture && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowManufacture(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-base font-semibold text-gray-900 mb-4">Log production — {item.name}</h2>
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">Quantity produced ({itemDbUnit})</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={manufactureQty}
                  onChange={(e) => { setManufactureQty(e.target.value); setShortfalls(null); }}
                  className={inputCls}
                  autoFocus
                />
              </div>

              {shortfalls && shortfalls.length > 0 && (
                <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                  <p className="text-xs font-medium text-amber-800 mb-1">You're short on:</p>
                  {shortfalls.map((s) => (
                    <p key={s.ingredient_id} className="text-xs text-amber-700">
                      {s.name}: need {s.required.toFixed(2)} {s.unit}, have {s.available.toFixed(2)} (short {s.deficit.toFixed(2)})
                    </p>
                  ))}
                  <p className="text-xs text-amber-600 mt-1">Continue anyway? Ingredients will go negative.</p>
                </div>
              )}

              {manufactureError && <p className="text-xs text-red-600 mb-3">{manufactureError}</p>}

              <div className="flex gap-2">
                {shortfalls ? (
                  <button
                    onClick={() => handleManufacture(true)}
                    disabled={manufacture.isPending}
                    className="flex-1 bg-amber-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                  >
                    {manufacture.isPending ? "Saving…" : "Confirm anyway"}
                  </button>
                ) : (
                  <button
                    onClick={() => handleManufacture(false)}
                    disabled={manufacture.isPending}
                    className="flex-1 bg-cyan-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-cyan-700 disabled:opacity-50"
                  >
                    {manufacture.isPending ? "Saving…" : "Log production"}
                  </button>
                )}
                <button
                  onClick={() => { setShowManufacture(false); setShortfalls(null); }}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Adjust stock */}
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">Adjust stock</p>
            <div className="flex gap-2">
              <button
                onClick={() => handleAdjust(itemDbUnit === "ml" ? -10 : -1)}
                className="px-3 h-8 rounded-full border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-sm font-medium"
              >
                −{itemDbUnit === "ml" ? 10 : 1} {itemDbUnit}
              </button>
              <button
                onClick={() => handleAdjust(itemDbUnit === "ml" ? 10 : 1)}
                className="px-3 h-8 rounded-full border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 text-sm font-medium"
              >
                +{itemDbUnit === "ml" ? 10 : 1} {itemDbUnit}
              </button>
              <button
                onClick={() => { setShowAdjustModal(true); setAdjustDisplayUnit(null); setAdjustDelta(itemDbUnit === "ml" ? "-10" : "-"); }}
                className="px-3 py-1 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
              >
                Custom
              </button>
            </div>
          </div>

          {showAdjustModal && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-2 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Amount (negative = remove)</label>
                  <input
                    type="number"
                    step="any"
                    value={adjustDelta}
                    onChange={(e) => setAdjustDelta(e.target.value)}
                    placeholder="e.g. -30"
                    className={inputCls}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Unit</label>
                  <UnitSelect
                    value={activeAdjustUnit}
                    onChange={(e) => setAdjustDisplayUnit(e.target.value)}
                    className={selectCls}
                  />
                  {storageHint(activeAdjustUnit) && (
                    <p className="text-xs text-gray-400 mt-1">{storageHint(activeAdjustUnit)}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Reason</label>
                  <select value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} className={selectCls}>
                    {ADJUST_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input type="text" value={adjustNotes} onChange={(e) => setAdjustNotes(e.target.value)} className={inputCls} />
              </div>
              {adjustError && <p className="text-xs text-red-600">{adjustError}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => handleAdjust()}
                  disabled={adjust.isPending}
                  className={`text-white rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${parseFloat(adjustDelta) >= 0 ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                >
                  {adjust.isPending ? "Saving…" : "Apply"}
                </button>
                <button
                  onClick={() => { setShowAdjustModal(false); setAdjustError(null); }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Usage log */}
        {logs.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
            <p className="text-sm font-medium text-gray-700 px-4 py-3 border-b border-gray-100">Usage log</p>
            <div className="divide-y divide-gray-50">
              {logs.map((log) => (
                <div key={log.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                  <div>
                    <span className={`font-medium ${log.change_amount < 0 ? "text-red-600" : "text-green-600"}`}>
                      {fmtDelta(log.change_amount, itemDbUnit)}
                    </span>
                    <span className="text-gray-400 mx-1">·</span>
                    <span className="text-gray-500 text-xs">{log.reason.replace(/_/g, " ")}</span>
                    {log.notes && <span className="text-gray-400 text-xs ml-1">— {log.notes}</span>}
                  </div>
                  <span className="text-xs text-gray-400">{fmt(log.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delete */}
        {!editing && (
          <div className="mt-6">
            {showDeleteConfirm ? (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between">
                <p className="text-sm text-red-700">Delete this item permanently?</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    disabled={deleteItem.isPending}
                    className="bg-red-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleteItem.isPending ? "Deleting…" : "Delete"}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Delete item
              </button>
            )}
          </div>
        )}
      </div>
    </LayoutWide>
  );
}
